import { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LOGO_SEAL_WHITE, AppContext, hasPageBackdrop } from "../App";

// Scroll distance past which the navbar leaves its transparent over-the-painting
// state and settles into the solid vellum bar. Tuned to feel responsive without
// flickering at the very top; kept in the 40–80px band.
const SCROLL_THRESHOLD = 56;

export default function Nav() {
  const { t, openEnquiry } = useContext(AppContext);
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // The seal (SVG) paints as soon as it's fetched, but the "ANACHRONA"
  // wordmark uses the Cinzel webfont and can flash a fallback font first —
  // hold the whole wordmark hidden until Cinzel is actually ready so both
  // appear together in the same frame, instead of the text catching up.
  const [fontsReady, setFontsReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    document.fonts.ready.then(() => { if (!cancelled) setFontsReady(true); });
    return () => { cancelled = true; };
  }, []);

  // Passive listener + rAF throttle (same approach as the parallax/backdrop work)
  // so scrolling stays jank-free.
  useEffect(() => {
    let ticking = false;
    const update = () => { setScrolled(window.scrollY > SCROLL_THRESHOLD); ticking = false; };
    const onScroll = () => {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    update(); // sync initial state (e.g. loading mid-page or after route change)
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Re-sync on route change: ScrollToTop resets to 0, and painted vs. non-painted
  // routes need different top-of-page treatments.
  useEffect(() => {
    setMobileOpen(false);
    setScrolled(window.scrollY > SCROLL_THRESHOLD);
  }, [location.pathname]);

  // Transparent, light-text navbar only at the top of the four painted-plane
  // routes. Opening the mobile menu snaps it solid so the dropdown reads cleanly.
  const transparent = hasPageBackdrop(location.pathname) && !scrolled && !mobileOpen;

  // On-plane (light) vs. solid (ink-on-vellum) treatments.
  const wordmarkColor = transparent ? "var(--vellum)" : "var(--ink)";
  const linkActive    = transparent ? "var(--gold-soft)" : "var(--ink)";
  const linkIdle      = transparent ? "rgba(248,246,240,0.75)" : "var(--ink-mute)";
  const linkUnderline = transparent ? "var(--gold-soft)" : "var(--gold-deep)";
  const ctaColor      = transparent ? "var(--vellum)" : "var(--ink)";
  const ctaBorder     = transparent ? "rgba(248,246,240,0.6)" : "var(--ink)";
  const ctaHoverBg    = transparent ? "var(--vellum)" : "var(--ink)";
  const ctaHoverText  = transparent ? "var(--ink)" : "var(--vellum)";
  const burgerColor   = transparent ? "var(--vellum)" : "var(--ink)";

  const links = [
    { id: "catalog",       label: t.navCatalog,       path: "/catalogue" },
    { id: "archive",       label: t.navArchive,        path: "/archive" },
    { id: "digital-twins", label: t.navDigitalTwins,  path: "/digital-twins" },
    { id: "about",         label: t.navAbout,          path: "/about" },
  ];

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const handleNav = (path) => {
    navigate(path);
    window.scrollTo(0, 0);
  };

  return (
    <nav style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
      background: transparent ? "transparent" : "var(--vellum)",
      borderBottom: transparent
        ? "1px solid transparent"
        : (scrolled ? "1px solid var(--rule-strong)" : "1px solid var(--rule)"),
      transition: "background-color 0.2s var(--ease), border-color 0.2s var(--ease)",
      padding: "0 var(--pad-x)",
      // Grows the fixed header on notched/Dynamic-Island devices so its content
      // clears the cutout; env() falls back to 0px everywhere else, so
      // non-notched mobile and desktop are unaffected.
      paddingTop: "env(safe-area-inset-top, 0px)",
    }}>
      <style>{`
        .desktop-nav { display: flex; }
        .mobile-burger { display: none; }
        @media (max-width: 767px) {
          .desktop-nav { display: none !important; }
          .mobile-burger { display: flex !important; }
        }
      `}</style>
      <div style={{
        maxWidth: 1180, margin: "0 auto",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        height: scrolled ? 68 : 92,
        transition: `height 0.4s var(--ease)`,
      }}>

        {/* Wordmark — hidden until Cinzel is ready so the seal and the text appear together */}
        <div onClick={() => handleNav("/")} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 12, opacity: fontsReady ? 1 : 0 }}>
          {/* Unfiltered on purpose: the asset is already gold (#c4a35a). The
              solid navbar used to darken it to near-black, which made the
              seal change colour between painted routes and plain ones like
              the archive. Left alone it reads the same gold everywhere. */}
          <img
            src={LOGO_SEAL_WHITE}
            alt=""
            style={{
              width: 52,
              height: 52,
            }}
          />
          <span style={{
            fontFamily: "'Cinzel', serif",
            fontSize: 20,
            fontWeight: 400,
            letterSpacing: "0.30em",
            textTransform: "uppercase",
            color: wordmarkColor,
            transition: "color 0.2s var(--ease)",
          }}>Anachrona</span>
        </div>

        {/* Desktop nav */}
        <div style={{ display: "flex", gap: 36, alignItems: "center" }} className="desktop-nav">
          {links.map(l => (
            <button key={l.id} onClick={() => handleNav(l.path)} style={{
              background: "none", border: 0, cursor: "pointer",
              fontFamily: "'EB Garamond', serif", fontSize: 12,
              letterSpacing: "0.22em", textTransform: "uppercase",
              color: isActive(l.path) ? linkActive : linkIdle,
              borderBottom: isActive(l.path) ? `1px solid ${linkUnderline}` : "1px solid transparent",
              padding: "4px 0",
              transition: "color 0.2s var(--ease)",
            }}>
              {l.label}
            </button>
          ))}
          <button
            onClick={() => openEnquiry("General")}
            style={{
              background: "transparent",
              border: `1px solid ${ctaBorder}`,
              padding: scrolled ? "6px 18px" : "8px 22px", marginLeft: 8,
              cursor: "pointer",
              fontFamily: "'EB Garamond', serif", fontSize: 12,
              letterSpacing: "0.22em", textTransform: "uppercase",
              color: ctaColor,
              transition: "background 0.2s var(--ease), color 0.2s var(--ease), border-color 0.2s var(--ease), padding 0.4s var(--ease)",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = ctaHoverBg; e.currentTarget.style.color = ctaHoverText; }}
            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = ctaColor; }}
          >
            {t.ctaBook}
          </button>
        </div>

        {/* Mobile burger */}
        <button
          className="mobile-burger"
          onClick={() => setMobileOpen(!mobileOpen)}
          style={{ background: "none", border: "none", cursor: "pointer", flexDirection: "column", gap: 5, padding: 8 }}
        >
          {[0, 1, 2].map(i => (
            <div key={i} style={{
              width: 22, height: 1, background: burgerColor,
              transition: "all 0.3s ease",
              transform: mobileOpen
                ? (i === 0 ? "rotate(45deg) translate(4px, 4px)" : i === 2 ? "rotate(-45deg) translate(4px, -4px)" : "scaleX(0)")
                : "none",
            }} />
          ))}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div style={{ background: "var(--bg)", borderTop: "1px solid var(--rule)", padding: "20px 0 30px" }}>
          {links.map(l => (
            <button key={l.id} onClick={() => handleNav(l.path)} style={{
              display: "block", width: "100%", background: "none", border: "none",
              fontFamily: "'EB Garamond', serif", fontSize: 16,
              letterSpacing: "0.22em", textTransform: "uppercase",
              padding: "12px 24px", textAlign: "left",
              color: isActive(l.path) ? "var(--ink)" : "var(--ink-mute)",
              cursor: "pointer",
            }}>
              {l.label}
            </button>
          ))}
          <button
            onClick={() => openEnquiry("General")}
            style={{
              display: "block", background: "none",
              border: "1px solid var(--ink)", padding: "8px 22px",
              fontFamily: "'EB Garamond', serif", fontSize: 12,
              letterSpacing: "0.22em", textTransform: "uppercase",
              color: "var(--ink)", cursor: "pointer", margin: "12px 24px",
            }}
          >
            {t.ctaBook}
          </button>
        </div>
      )}
    </nav>
  );
}
